import React, { useEffect } from 'react'
import ReactDOM from 'react-dom'
import { useLang } from '~/hooks/useLang'
import useSystemState from '~/features/System/stores/SystemState'
import StandardModal from '~/components/StandardModal'
import {
  Grid,
  IconButton,
  Button,
  CircularProgress,
  Autocomplete,
  TextField,
} from '@mui/material'
import { storeScreenSettings } from '~/features/System/services'
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { Search } from '@mui/icons-material'
import { Downgraded, useHookstate } from '@hookstate/core'
import { useMarkerState } from '../../stores/MapState'
import { Dayjs, ManipulateType } from 'dayjs'
import { getMarkersDeviceFilteredHistoricalService, getMarkersDispatchFilteredHistoricalService } from '../../services/request'
import type ScreenSettingsDraggableProps from '~/features/System/interfaces/ScreenSettings'
import { authFetch } from '~/services/fetch'
import { useLocation } from 'react-router-dom'

const modal = document.getElementById('div-modal') as HTMLElement

// eslint-disable-next-line max-lines-per-function
export default function MapHistoricFiltersModalDraggable() {
  const { translate } = useLang()
  const location = useLocation();
  const { windowManager } = useSystemState()
  const { markersState, loadingRouteDevice, popupMarker } = useMarkerState()
  const { dateTimeHistoric, forceClosePopup } = useMarkerState();
  const deviceRouteCurrent = useHookstate<string | null>(null)
  const loading = useHookstate<boolean>(false)
  const loadingComponent = useHookstate(true)
  const period = useHookstate<string | null>(null)
  const options = ['minute', 'hour', 'day']
  const dateFilter = useHookstate<Dayjs | null>(null)


  const getPositionsDraggable = useHookstate<ScreenSettingsDraggableProps>({
    id: 0,
    user_id: 0,
    url: '',
    window_name: '',
    x_position: 80,
    y_position: 141,
    is_window_open: true,
    is_window_minimized: false,
    created_at: new Date().toJSON(),
    updated_at: new Date().toJSON(),
    deleted_at: null
  })

  useEffect(() => {
    authFetch({
      url: '/screen-setting/get-user-settings',
      method: 'POST',
      data: {
        url: location.pathname,
      }
    }).then((response) => {
      if ('MapHistoricFiltersModalDraggable' in response.data) {
        getPositionsDraggable.set(response.data.MapHistoricFiltersModalDraggable)
      }
      loadingComponent.set(false)
    })

    return () => {
      windowManager()['mapHistoricFilters'].open.set(false)
    }
  }, [])

  const handleStop = (event, dragElement) => {
    event.stopPropagation()

    windowManager()['mapHistoricFilters'].merge({
      positionX: dragElement.x,
      positionY: dragElement.y,
    })
  }

  useEffect(() => {
    return () => {
      storeScreenSettings({
        windowName: 'MapHistoricFiltersModalDraggable',
        isOpen: windowManager()['mapHistoricFilters'].open.get(),
        isMinimized: windowManager()['mapHistoricFilters'].minimized.get(),
        positionX: windowManager()['mapHistoricFilters'].positionX.get(),
        positionY: windowManager()['mapHistoricFilters'].positionY.get(),
      })
    }
  }, [
    windowManager()['mapHistoricFilters'].open.get(),
    windowManager()['mapHistoricFilters'].minimized.get(),
    windowManager()['mapHistoricFilters'].positionY.get(),
    windowManager()['mapHistoricFilters'].positionX.get(),
  ])

  const searchHistorical = () => {
    loadingRouteDevice.set(!loadingRouteDevice.get())
    deviceRouteCurrent.set(null)

    windowManager()['mapSetting']['markersView']['deviceHistoric'].show.set(false)
    windowManager()['mapSetting']['markersView']['dispatchHistoric'].show.set(false)
    loading.set(true)
    dateTimeHistoric.set(dateFilter.get())
    getMarkersDispatchFilteredHistoricalService({
      dateTime: dateTimeHistoric.attach(Downgraded).get()
    }).then((response) => {
      markersState()['dispatchHistoric']?.set(response)
      windowManager()['mapSetting']['markersView']['dispatchHistoric'].show.set(true)
    }).finally(() => {
      forceClosePopup.set(!forceClosePopup.get())
      loading.set(false)
    })

    getMarkersDeviceFilteredHistoricalService({
      dateTime: dateTimeHistoric.attach(Downgraded).get()
    }).then((response) => {
      markersState()['deviceHistoric']?.set(response)
      windowManager()['mapSetting']['markersView']['deviceHistoric'].show.set(true)
    }).finally(() => {
      forceClosePopup.set(!forceClosePopup.get())
      loading.set(false)
    })
  }

  useEffect(() => {
    if (popupMarker.get() && popupMarker?.get()['markerInfo']?.identifier) {
      const currentRouteDevice = popupMarker?.get()['markerInfo']?.identifier
      deviceRouteCurrent.set(currentRouteDevice)
    }
  }, [popupMarker['traceRoute']?.get()])


  return ReactDOM.createPortal(
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {windowManager()['mapHistoricFilters'].open.value && !loadingComponent.get() &&
        <StandardModal
          title={translate('Historic Search')}
          handleStop={handleStop}
          position={{
            x: windowManager()['mapHistoricFilters'].positionX?.get()
              ? Number(windowManager()['mapHistoricFilters'].positionX?.get())
              : 80,
            y: windowManager()['mapHistoricFilters'].positionY?.get()
              ? Number(windowManager()['mapHistoricFilters'].positionY?.get())
              : 141,
          }}
          closeButton={
            !windowManager()['mapHistoricFilters'].disabled.value
              ? () => windowManager()['mapHistoricFilters'].open.set(false)
              : false
          }
          isExpanded={windowManager()['mapHistoricFilters'].minimized}
          dimensions={{
            width: 500,
          }}
        >
          <Grid container display={'flex'} flexDirection={'row'}>
            <Grid item xs={12}>
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale={'pt-br'}
              >
                <DateTimePicker
                  format="DD/MM/YYYY HH:mm"
                  views={[
                    'year',
                    'month',
                    'day',
                    'hours',
                    'minutes',
                  ]}
                  value={dateFilter.attach(Downgraded).get()}
                  onChange={(value) => dateFilter.set(value)}
                  slotProps={{
                    textField: {
                      inputProps: {
                        placeholder: translate(`Historic Date`),
                      },
                    },
                  }}
                  disableFuture
                  sx={{ width: '100%' }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid
              item
              xs={8}
              display={'flex'}
              sx={{ gap: 1, pt: 2 }}
            >
              <Autocomplete
                options={options}
                size='small'
                value={period.get()}
                getOptionLabel={(option) => translate(option)}
                renderInput={(params) => (
                  <TextField {...params} label={translate('Period')} />
                )}
                onChange={(_, value) => period.set(value)}
                sx={{ width: '50%' }}
              />
              <Button
                title={translate('Subtract period')}
                variant='contained'
                size="small"
                onClick={() =>
                  dateFilter.set(
                    dateFilter
                      .get()
                      ?.subtract(1, period.get() as ManipulateType) ?? null
                  )
                }
                sx={{ fontSize: 16 }}
              >
                -1
              </Button>
              <Button
                title={translate('Add period')}
                variant='contained'
                size="small"
                onClick={() =>
                  dateFilter.set(
                    dateFilter.get()?.add(1, period.get() as ManipulateType) ??
                    null
                  )
                }
                sx={{ fontSize: 16 }}
              >
                +1
              </Button>

            </Grid>
            <Grid
              item
              xs={4}
              display={'flex'}
              justifyContent={'end'}
              alignItems={'flex-end'}
              sx={{ pt: 2 }}
            >
              <Button
                variant="text"
                onClick={() => {
                  dateFilter.set(null)
                  period.set(null)
                }}
                sx={{
                  height: '40px',
                  textTransform: 'none',
                  textDecorationLine: 'underline',
                  color: 'text.primary',
                  ':hover': {
                    bgcolor: 'transparent',
                    color: 'primary.main',
                  },
                }}
              >
                {translate('Clear filters')}
              </Button>
              <IconButton
                onClick={() => searchHistorical()}
                disabled={!dateFilter.get()}
                title={translate('Search')}
              >
                {loading.get() ? <CircularProgress size={30} /> : <Search />}
              </IconButton>
            </Grid>
            {deviceRouteCurrent.get() &&
              <Grid container marginTop={2}>
                <Button
                  variant='contained'
                  onClick={() => {
                    loadingRouteDevice.set(!loadingRouteDevice.get())
                    deviceRouteCurrent.set(null)
                  }}>
                  {`${translate('Clear device route', { identifier: deviceRouteCurrent?.get() })}`}
                </Button>
              </Grid>
            }
          </Grid>
        </StandardModal>
      }
    </>,
    modal
  )
}
